<template>
  <form class="form-field" @submit.prevent>
    <BaseInput v-model="form.label" name="label" validate="required" @input="autocompleteKeyform" label="Label"/>
    <BaseInput v-model="form.description" label="Description" hint="Short hint to understand the purpose"/>
    <BaseInput
      v-if="showKey"
      :class="{'form-field__repeater-indicator': parent && parent.type === 'repeater'}"
      :value="form.key"
      @input="onKeyInput"
      label="Key"
      :meta="metaKey"
      name="key"
      :validate="keyValidation"
      :isLocked="isKeyFieldLocked"
      @click.native="onLockedFieldClick"
      />
    <BaseSelect
      v-if="showTypeSelect"
      v-model="form.type"
      :options="SELECT_OPTIONS"
      groupedOptions
      label="Type"
      name="type"
      placeholder="Select..."
      :hint="form.type && TYPES_DATA[form.type] ? TYPES_DATA[form.type].hint : null"
      :learnAction="showLearnModal"
      :learnLabel="selectedTypeLabel && `Learn about ${selectedTypeLabel} field`"
      :theme="selectDisabled ? 'disabled' : null"
      validate="required"
    ></BaseSelect>
    <BaseCollapse :changeHeight="doUpdate" :transitionDuration="0.6">
      <component v-if="subFormExists" v-model="form.subForm" :is="subFormComponent" @update="onUpdate"></component>
    </BaseCollapse>
  </form>
</template>

<script>
  import SubForms from '@/components/forms/sub-forms'
  import { SELECT_OPTIONS, TYPES_DATA } from './field-types.js'
  import { cloneDeep, kebabCase, upperFirst, camelCase, get, keyBy } from 'lodash'
  import { MDSModalConfirmDecisionModel, MDSModalIframeModel } from '@/models/modal-datasets.model'
  import { generateUuid } from '@/helpers'

  export default {
    inject: ['$validator'],
    components: {
      ...SubForms
    },
    props: {
      value: Object,
      scope: String,
      forbiddenKeys: {
        type: Array,
        default: () => [],
      },
      parent: {
        type: Object,
        default: () => { return {} }
      },
      showTypeSelect: {
        type: Boolean,
        default: true
      },
      showKey: {
        type: Boolean,
        default: true
      },
      showKeyHint: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        doUpdate: false,
        selectDisabled: false,
        keyTouched: false,
        keyValidation: {
          required: true,
          excluded: this.forbiddenKeys,
          min: 3,
          max: 10
        },
        form: {
          key: null,
          label: null,
          description: null,
          type: null,
          subForm: {}
        },
        isKeyFieldLocked: false,
        SELECT_OPTIONS: cloneDeep(SELECT_OPTIONS),
        TYPES_DATA: cloneDeep(TYPES_DATA),
        savedInDatabaseData: {
          fieldType: '',
          subForm: {}
        },
      }
    },
    watch: {
      'value': {
        handler (val) {
          if (val) {
            this.keyTouched = true
            Object.assign(this.form, val)
          }
        },
        immediate: true
      },
      'form': {
        handler (val) {
          this.$emit('input', val)
        },
        deep: true
      },
      'form.type': {
        handler (val) {
          if (val === this.savedInDatabaseData.fieldType) {
            this.restoreDatabaseBackup()
          } else {
            this.resetSubForm()
          }
        }
      },
      subFormComponent() {
        this.onUpdate()
      }
    },
    computed: {
      metaKey () {
        if (!this.showKeyHint) {
          return ''
        }
        
        const { key } = this.form
        let scope = this.scope.replace(/s$/, '');
        let namespace = process.env.META_NAMESPACE
      
        switch (scope) {
          case 'variant':
            scope = 'product.selected_variant'
            break
          case 'customobjectsgroup':
            scope = 'shop'
            namespace += '_co_groups'
            break
        }
        
        return `${scope}.metafields.${namespace}${key ? `.<strong>${key}</strong>` : ''}`
      },
      subFormComponent () {
        const mapping = {
          SubFormRepeaterJson: 'SubFormRepeater',
        }
        const componentName = 'SubForm' + upperFirst(camelCase(this.form.type))
        
        return mapping[componentName] || componentName
      },
      subFormExists () {
        return get(this.$options.components, this.subFormComponent)
      },
      learnUrl () {
        return this.form.type ? `https://airfields.io/article/field-type-${kebabCase(this.form.type)}` : ''
      },
      selectedTypeLabel () {
        const { type } = this.form
        for (let i = 0, len = this.SELECT_OPTIONS.length; i < len; i++) {
          var found = get(keyBy(this.SELECT_OPTIONS[i].options, 'value')[type], 'label')
          if (found) return found
        }
      }
    },
    methods: {
      formatKey(str) {
        return str.toLowerCase().replace(/\s/g, '_')
      },
      autocompleteKeyform (str) {
        if(!this.keyTouched) {
          this.form.key = this.formatKey(str).slice(0, this.keyValidation.max)
        }
      },
      onKeyInput (str) {
        this.keyTouched = true
        this.form.key = this.formatKey(str)
        // Key field validate on 'input instead of default 'blur' > for better user experience
        this.$validator.validate('key')
      },
      formatAllowedOptions () {
        if (this.form.type && (this.form.type === 'group' || this.form.type === 'repeater')) {
            this.selectDisabled = true
        }

        this.SELECT_OPTIONS[0].options = this.SELECT_OPTIONS[0].options.map(item => {
          if (( this.parent.type === 'repeater' || this.parent.type === 'repeater_json') && (item.value === 'repeater' || item.value === 'repeater_json' || item.value ==='group')) {
            item.disabled = true
          }
          else if (this.parent.type === 'group' && item.value === 'group') {
            item.disabled = true
          }

          return item
        })
      },
      resetSubForm () {
        this.form.subForm = {}
      },
      onUpdate () {
        this.doUpdate = generateUuid()
      },
      showLearnModal () {
        const props = new MDSModalIframeModel({
          src: this.learnUrl
        })
        this.$store.commit('modals/OPEN_MODAL', { name: 'ModalIframe', props })
      },
      findIfKeyExistsInDatabase () {
        if (this.value) {
          this.isKeyFieldLocked = !!this.value.key
        }
      },
      unlockField () {
        this.isKeyFieldLocked = false
      },
      onLockedFieldClick () {
        if (!this.isKeyFieldLocked) return

        const props = new MDSModalConfirmDecisionModel ({
          confirmationHandler: this.unlockField,
          title: 'Edit field key',
          description: `Changing the <strong>key</strong> of a published field will interrupt the delivery of your content to applications that currently display it. Change the field <strong>key</strong> only if you plan to update your client applications as well.`,
          btnTextConfirm: `Edit field key`
        })
        this.$store.commit('modals/OPEN_MODAL', { name: 'ModalConfirmDecision', props })
      },
      backupSavedInDatabaseData () {
        Object.assign(this.savedInDatabaseData, {
          fieldType: this.form.type,
          subForm: this.form.subForm,
        })
      },
      restoreDatabaseBackup () {
        Object.assign(this.form.subForm, this.savedInDatabaseData.subForm)
      }
    },
    created () {
      this.formatAllowedOptions()
      this.findIfKeyExistsInDatabase()
      this.backupSavedInDatabaseData()
    }
  }
</script>

<style lang="scss">
  @import './src/assets/styles/colors.scss';
  @import './src/assets/styles/fonts.scss';
  @import './sub-forms/sub-forms.scss';

  .form-field {

    // focused purple border cut fix: 
    .BaseCollapse {
      padding: 0 1px;
    }

    &__repeater-indicator {

      .base-input__wrapper {
        position: relative;
        
        &:before {
          content: '[i]';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          background-color: #d4d4d433;
          border-radius: 3px;
          color: $color-regent-gray;
          font-family: $font-secondary;
          font-size: 14px;
        }
      }
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/forms/FormField.vue